/**
 * Brand tokens for Antoja ("versión sobria", closed on 2026-10-28).
 *
 * Rules from the brand canvas:
 * - One color only, used for the main action, the logo dot and the app icon.
 * - Chontaduro always carries black text on top (6.09:1).
 * - Every text pair passes WCAG AA in both themes (see theme.test.ts).
 * - Dark theme "Noche de salsa" follows the phone's dark mode.
 *
 * Both apps read these values; nothing else in the repo should hard-code a color.
 */

export type ColorScheme = 'light' | 'dark';

export interface ThemeColors {
  /** Screen background */
  readonly bg: string;
  /** Fields and tiles */
  readonly fill: string;
  /** Borders and dividers */
  readonly line: string;
  /** Border of form controls: at least 3:1 against bg and fill (WCAG 1.4.11) */
  readonly field: string;
  /** Main text */
  readonly ink: string;
  /** Secondary text */
  readonly muted: string;
  /** Main action (one per screen) */
  readonly primary: string;
  /** Text and icons on the main action */
  readonly onPrimary: string;
  /** The dot in the "antoja•" wordmark */
  readonly logoDot: string;
}

/** Raw brand colors. Prefer the semantic tokens in `colors`. */
export const brand = {
  /** Chontaduro: main action, logo dot and icon in the light theme */
  chontaduro: '#EE6A3F',
  /** Black text that always sits on chontaduro */
  onChontaduro: '#111111',
  /** Muted chontaduro: only the logo dot in the dark theme */
  chontaduroMuted: '#D07A5C',
} as const;

export const colors = {
  light: {
    bg: '#FFFFFF',
    fill: '#F4F4F3',
    line: '#E8E8E6',
    field: '#8A8A8A',
    ink: '#111111',
    muted: '#6B6B6B',
    primary: brand.chontaduro,
    onPrimary: brand.onChontaduro,
    logoDot: brand.chontaduro,
  },
  /** "Noche de salsa" */
  dark: {
    bg: '#16111C',
    fill: '#221B2A',
    line: '#2E2638',
    field: '#857A91',
    ink: '#F7EEF3',
    muted: '#A89DB2',
    primary: '#E9DEE6',
    onPrimary: '#16111C',
    logoDot: brand.chontaduroMuted,
  },
} as const satisfies Record<ColorScheme, ThemeColors>;

export const typography = {
  /** Geist (Google Fonts, OFL). One family for everything. */
  fontFamily: 'Geist',
  fontFallback: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  fontWeights: {
    regular: 400,
    medium: 500,
    semibold: 600,
  },
  /** Headings use negative tracking, between -0.03em and -0.055em. */
  headingLetterSpacingEm: -0.03,
} as const;

export type FontWeightName = keyof typeof typography.fontWeights;

/**
 * Tokens for the system color scheme. Anything other than "dark" (null, undefined,
 * React Native's "unspecified") gets the light theme.
 */
export function getThemeColors(scheme: string | null | undefined): ThemeColors {
  return scheme === 'dark' ? colors.dark : colors.light;
}

/** CSS custom property name for a token, e.g. `--antoja-on-primary`. */
export function cssVariableName(token: keyof ThemeColors): string {
  return `--antoja-${token.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`;
}

/** CSS custom property that holds the font stack. */
export const FONT_VARIABLE = '--antoja-font-sans';

/** `"Geist", <fallbacks>` for CSS. */
export function fontStack(): string {
  return `"${typography.fontFamily}", ${typography.fontFallback}`;
}

function declarations(theme: ThemeColors): string {
  return (Object.keys(theme) as (keyof ThemeColors)[])
    .map((token) => `${cssVariableName(token)}:${theme[token]};`)
    .join('');
}

/**
 * Stylesheet with every token as a CSS variable: light by default, "Noche de salsa"
 * when the system asks for dark mode. Used by the console (Tailwind reads the variables).
 */
export function buildThemeCss(): string {
  return [
    `:root{color-scheme:light dark;${FONT_VARIABLE}:${fontStack()};${declarations(colors.light)}}`,
    `@media (prefers-color-scheme: dark){:root{${declarations(colors.dark)}}}`,
  ].join('\n');
}

/** WCAG 2.x relative luminance of a #RRGGBB color. */
export function relativeLuminance(hex: string): number {
  const match = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!match?.[1]) {
    throw new Error(`Expected a #RRGGBB color, got "${hex}"`);
  }
  const value = match[1];
  const channel = (offset: number): number => {
    const c = Number.parseInt(value.slice(offset, offset + 2), 16) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
}

/** WCAG 2.x contrast ratio between two #RRGGBB colors (1 to 21). */
export function contrastRatio(foreground: string, background: string): number {
  const a = relativeLuminance(foreground);
  const b = relativeLuminance(background);
  const [lighter, darker] = a > b ? [a, b] : [b, a];
  return (lighter + 0.05) / (darker + 0.05);
}
